import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { cartService } from '../services/cartService';
import { handleCreateNewPriceAsyncService, registerObserver, unRegisterObserver, clearPricesAsyncService } from './sharedComponents/priceAsyncService';
import { cartManager, sortByQuantity, createPriceUniqueKey } from './sharedComponents/cartManager';
import { getFetchRequest, postFetchRequest, GET_CURRENT_USER, MAKE_USER_REQUEST } from '../store/requests';
import { makeUrl, handleGetImageUrl, EMPTY_IMAGE_SRC, errorCallback } from '../const/const';
import { Link, useNavigate } from 'react-router-dom';

const ChevronIconNavigation = ({ nameOfClass }) => (
    <svg className={nameOfClass} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 6.37 12" width="8" height="16">
        <g id="a"></g>
        <g id="b">
            <g id="c">
                <path className="d" d="M6.23,5.52L.85,.15C.66-.05,.34-.05,.15,.15S-.05,.66,.15,.85L5.29,6,.15,11.15c-.2,.2-.2,.51,0,.71,.1,.1,.23,.15,.35,.15s.26-.05,.35-.15L6.23,6.48c.13-.13,.17-.31,.12-.48,.04-.17,0-.35-.12-.48Z"></path>
            </g>
        </g>
    </svg>
);

// Компонент для отображения ценовых уровней (как в ProductItem.js)
const PricesList = ({ pricesArray, activePricesChanged, currentQuantity, duty, stockCoefs, blockedStocks, productPriceId, currencySign = '₽' }) => {
    console.log(`🎨 [PricesList] Component received:`, {
        pricesArrayLength: pricesArray?.length || 0,
        firstSupplier: pricesArray?.[0]?.distributor || pricesArray?.[0]?.source,
        firstProductId: pricesArray?.[0]?.productId,
        firstPriceValue: pricesArray?.[0]?.quantities?.[0]?.priceValue,
        productPriceId,
        duty,
    });

    const parseDuty = (dutyString) => {
        if (!dutyString) return 0;
        const match = dutyString?.match(/(\d+\.?\d*)/);
        return match ? parseFloat(match[1]) / 100 : 0;
    };

    const calculatePriceWithDuty = (priceValue, duty) => {
        const dutyFactor = parseDuty(duty);
        const finalPrice = priceValue * (1 + dutyFactor);
        return finalPrice;
    };

    if (!pricesArray || pricesArray.length === 0) {
        return <div className="price-tier">Загрузка цен...</div>;
    }

    // НОВАЯ ЛОГИКА: Находим поставщика по productPriceId (как на странице товара)
    console.log(`🔍 [PricesList SEARCH] Looking for productPriceId: ${productPriceId} in ${pricesArray.length} suppliers`);
    
    let selectedSupplier = null;
    if (productPriceId) {
        // ДЕТАЛЬНЫЙ ПОИСК ПО ВСЕМ ПОСТАВЩИКАМ
        pricesArray.forEach((supplier, index) => {
            console.log(`🏢 [SUPPLIER SEARCH ${index}] ${supplier.distributor || supplier.source}:`, {
                quantitiesCount: supplier.quantities?.length || 0,
                allPriceIds: supplier.quantities?.map(q => q.id) || [],
                searchingFor: productPriceId,
            });
        });
        
        // Ищем поставщика, у которого есть этот priceId
        for (const supplier of pricesArray) {
            const foundQuantity = supplier.quantities?.find(q => q.id === productPriceId);
            if (foundQuantity) {
                selectedSupplier = supplier;
                console.log(`🎯 [PricesList] Found supplier for priceId ${productPriceId}:`, {
                    supplier: supplier.distributor || supplier.source,
                    quantitiesCount: supplier.quantities.length,
                    foundQuantity: foundQuantity,
                });
                break;
            }
        }
        
        if (!selectedSupplier) {
            console.log(`⚠️ [PricesList] NO SUPPLIER FOUND for productPriceId: ${productPriceId}`);
        }
    } else {
        console.log(`⚠️ [PricesList] No productPriceId provided`);
    }

    // Если не нашли по productPriceId, берем первого доступного поставщика
    if (!selectedSupplier) {
        const filteredPricesArray = pricesArray.filter(price =>
            !blockedStocks.includes(price.distributor || price.source),
        );
        
        if (filteredPricesArray.length === 0) {
            return <div className="price-tier">Все склады заблокированы</div>;
        }
        
        // 🔧 ВРЕМЕННОЕ РЕШЕНИЕ: ищем DigiKey по имени поставщика (как на странице товара)
        const digiKeySupplier = filteredPricesArray.find(price => 
            (price.distributor || price.source) === 'DigiKey',
        );
        
        if (digiKeySupplier) {
            selectedSupplier = digiKeySupplier;
            console.log(`🎯 [PricesList] WORKAROUND: Found DigiKey by name (productPriceId missing from backend):`, {
                supplier: selectedSupplier.distributor || selectedSupplier.source,
            });
        } else {
            selectedSupplier = filteredPricesArray[0];
            console.log(`🎯 [PricesList] No DigiKey found, using first available:`, {
                supplier: selectedSupplier.distributor || selectedSupplier.source,
            });
        }
    }

    // Проверяем, что поставщик не заблокирован
    const supplierKey = selectedSupplier.distributor || selectedSupplier.source;
    if (blockedStocks.includes(supplierKey)) {
        return <div className="price-tier">Выбранный поставщик заблокирован</div>;
    }

    // Обрабатываем цены от выбранного поставщика (как на странице товара)
    const coef = stockCoefs[supplierKey] ?? 1;
    console.log(`🔧 [${supplierKey}] coef=${coef}, originalQuantities:`, selectedSupplier.quantities.length);
    
    const processedPrices = selectedSupplier.quantities.map(priceLevel => {
        // Применяем коэффициент склада
        const adjustedPrice = priceLevel.priceValue * coef;
        
        // Применяем пошлину (как в renderResponsiveTableRow на странице товара)
        const dutyFactor = parseDuty(duty);
        const priceWithFirstDuty = adjustedPrice * (1 + dutyFactor);
        
        console.log(`🔧 [${supplierKey}] ${priceLevel.minQty}+ ${priceLevel.priceValue} * ${coef} * (1 + ${dutyFactor}) = ${priceWithFirstDuty}`);
        
        return {
            ...priceLevel,
            priceValue: priceWithFirstDuty,
            supplier: supplierKey,
            manufacturer: selectedSupplier.manufacturer,
        };
    });

    const sortedPrices = processedPrices.sort((a, b) => a.minQty - b.minQty);

    // Определяем единственный активный уровень: максимальный minQty, который не превышает текущее количество
    const eligiblePrices = sortedPrices.filter(p => p.minQty <= currentQuantity);
    const activeMinQty = eligiblePrices.length > 0
        ? Math.max(...eligiblePrices.map(p => p.minQty))
        : (sortedPrices[0]?.minQty ?? null);

    return (
        <div className="price-tiers">
            {sortedPrices.map(priceLevel => {
                const isActive = activeMinQty !== null && priceLevel.minQty === activeMinQty;
                
                // НЕ применяем пошлину повторно - она уже включена в priceLevel.priceValue
                const priceWithDuty = priceLevel.priceValue;
                
                return (
                    <div
                        key={`${priceLevel.minQty}-${activePricesChanged}`}
                        className={`price-tier ${isActive ? 'price-tier_active' : ''}`}
                    >
                        <span className="tier-qty">{priceLevel.minQty}+</span>
                        <span className="tier-price">{priceWithDuty.toFixed(2)}{priceLevel.currency === 'USD' ? '$' : currencySign}</span>
                    </div>
                );
            })}
        </div>
    );
};

const Cart = ({ isCollapsed, cartItems = [] }) => {
    const navigate = useNavigate();
    const [order, setOrder] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [submittingOrder, setSubmittingOrder] = useState(false);
    
    // Данные для блока "Покупатель/Получатель/Доставка" подтягиваем из настроек
    const [settingsUserId, setSettingsUserId] = useState('');
    const [priceCurrency, setPriceCurrency] = useState('RUB');
    const [buyerName, setBuyerName] = useState('');
    const [recipientName, setRecipientName] = useState('');
    const [recipientPhone, setRecipientPhone] = useState('');
    const [deliveryAddress, setDeliveryAddress] = useState('');
    const [profileName, setProfileName] = useState('');
    const [profilePhone, setProfilePhone] = useState('');
    
    // WebSocket состояние для цен
    const [productPrices, setProductPrices] = useState({}); // {productId: pricesArray}
    const [pricesLoading, setPricesLoading] = useState(new Set()); // Set productId которые загружаются
    const [activePricesChanged, setActivePricesChanged] = useState(false);
    
    // 🔒 Резервная копия цен для восстановления после удаления
    const pricesBackupRef = useRef({});
    
    // Коэффициенты складов и заблокированные склады (как на странице товара)
    const [stockCoefs, setStockCoefs] = useState({});
    const [blockedStocks, setBlockedStocks] = useState([]);
    
    // 🔧 DEBOUNCE MAP для предотвращения множественных API запросов
    const quantityTimeouts = useMemo(() => new Map(), []);

    // 🏪 ФУНКЦИИ ДЛЯ ИЗВЛЕЧЕНИЯ ДАННЫХ О ТОВАРЕ ИЗ ЦЕН
    
    // Получить данные о наличии для товара из цен выбранного поставщика
    const getProductAvailability = useCallback((productId, productPriceId) => {
        const pricesArray = productPrices[productId] || [];
        if (pricesArray.length === 0) return { stockNumber: null, isLoading: true };
        
        // Ищем поставщика с нужным productPriceId
        let selectedSupplier = null;
        if (productPriceId) {
            for (const price of pricesArray) {
                if (price.quantities && Array.isArray(price.quantities)) {
                    const foundQuantity = price.quantities.find(q => q.id === productPriceId);
                    if (foundQuantity) {
                        selectedSupplier = price;
                        break;
                    }
                }
            }
        }
        
        // Если не нашли по productPriceId, используем DigiKey или первого доступного
        if (!selectedSupplier) {
            const filteredPricesArray = pricesArray.filter(price =>
                !blockedStocks.includes(price.distributor || price.source),
            );
            selectedSupplier = filteredPricesArray.find(price =>
                (price.distributor || price.source) === 'DigiKey',
            ) || filteredPricesArray[0];
        }
        
        return {
            stockNumber: selectedSupplier?.stockNumber || null,
            isLoading: false,
        };
    }, [productPrices, blockedStocks]);
    
    // Получить правила заказа для товара из цен выбранного поставщика
    const getProductOrderRules = useCallback((productId, productPriceId) => {
        const pricesArray = productPrices[productId] || [];
        if (pricesArray.length === 0) return { minOrderQuantity: null, multiplicity: null, isLoading: true };
        
        // Ищем поставщика с нужным productPriceId
        let selectedSupplier = null;
        if (productPriceId) {
            for (const price of pricesArray) {
                if (price.quantities && Array.isArray(price.quantities)) {
                    const foundQuantity = price.quantities.find(q => q.id === productPriceId);
                    if (foundQuantity) {
                        selectedSupplier = price;
                        break;
                    }
                }
            }
        }
        
        // Если не нашли по productPriceId, используем DigiKey или первого доступного
        if (!selectedSupplier) {
            const filteredPricesArray = pricesArray.filter(price =>
                !blockedStocks.includes(price.distributor || price.source),
            );
            selectedSupplier = filteredPricesArray.find(price =>
                (price.distributor || price.source) === 'DigiKey',
            ) || filteredPricesArray[0];
        }
        
        if (!selectedSupplier || !selectedSupplier.quantities || selectedSupplier.quantities.length === 0) {
            return { minOrderQuantity: null, multiplicity: null, isLoading: false };
        }
        
        // Определяем минимальный заказ как минимальный порог minQty (не разницу ценовых уровней)
        const sortedQuantities = Array.isArray(selectedSupplier?.quantities)
            ? [...selectedSupplier.quantities].sort(sortByQuantity)
            : [];
        const minOrderQuantity = sortedQuantities.length > 0 ? sortedQuantities[0].minQty : null;
        
        // Кратность равна минимальному заказу (если продается от 1 шт — кратность 1)
        const multiplicity = minOrderQuantity ?? 1;
        
        return {
            minOrderQuantity,
            multiplicity,
            isLoading: false,
        };
    }, [productPrices, blockedStocks]);
    
    // Получить информацию об отгрузке для товара из цен выбранного поставщика
    const getProductShipmentInfo = useCallback((productId, productPriceId) => {
        const pricesArray = productPrices[productId] || [];
        if (pricesArray.length === 0) return { distributor: null, packer: null, deliveryTime: null, isLoading: true };
        
        // Ищем поставщика с нужным productPriceId
        let selectedSupplier = null;
        if (productPriceId) {
            for (const price of pricesArray) {
                if (price.quantities && Array.isArray(price.quantities)) {
                    const foundQuantity = price.quantities.find(q => q.id === productPriceId);
                    if (foundQuantity) {
                        selectedSupplier = price;
                        break;
                    }
                }
            }
        }
        
        // Если не нашли по productPriceId, используем DigiKey или первого доступного
        if (!selectedSupplier) {
            const filteredPricesArray = pricesArray.filter(price =>
                !blockedStocks.includes(price.distributor || price.source),
            );
            selectedSupplier = filteredPricesArray.find(price =>
                (price.distributor || price.source) === 'DigiKey',
            ) || filteredPricesArray[0];
        }
        
        if (!selectedSupplier) {
            return { distributor: null, packer: null, deliveryTime: null, isLoading: false };
        }
        
        const distributor = selectedSupplier.distributor || selectedSupplier.source;
        const packer = selectedSupplier.packer;
        
        // Определяем время доставки на основе наличия
        const stockNumber = selectedSupplier.stockNumber || 0;
        const deliveryTime = stockNumber > 50 ? 'Склад' : '20-30 раб. дней';
        
        return {
            distributor,
            packer,
            deliveryTime,
            isLoading: false,
        };
    }, [productPrices, blockedStocks]);
    
    // Получить производителя товара из цен или существующих данных
    const getProductManufacturer = useCallback((productId, productPriceId, existingManufacturer) => {
        // Если производитель уже есть в данных товара - используем его
        if (existingManufacturer && existingManufacturer.trim() !== '') {
            return { manufacturer: existingManufacturer, isLoading: false };
        }
        
        const pricesArray = productPrices[productId] || [];
        if (pricesArray.length === 0) return { manufacturer: null, isLoading: true };
        
        // Ищем поставщика с нужным productPriceId
        let selectedSupplier = null;
        if (productPriceId) {
            for (const price of pricesArray) {
                if (price.quantities && Array.isArray(price.quantities)) {
                    const foundQuantity = price.quantities.find(q => q.id === productPriceId);
                    if (foundQuantity) {
                        selectedSupplier = price;
                        break;
                    }
                }
            }
        }
        
        // Если не нашли по productPriceId, используем DigiKey или первого доступного
        if (!selectedSupplier) {
            const filteredPricesArray = pricesArray.filter(price =>
                !blockedStocks.includes(price.distributor || price.source),
            );
            selectedSupplier = filteredPricesArray.find(price =>
                (price.distributor || price.source) === 'DigiKey',
            ) || filteredPricesArray[0];
        }
        
        const manufacturer = selectedSupplier?.manufacturer || null;
        
        return {
            manufacturer,
            isLoading: false,
        };
    }, [productPrices, blockedStocks]);
    
    // 🚫 БЛОКИРОВКА СИНХРОНИЗАЦИИ после удаления товаров (предотвращает подвисания)
    const [lastDeleteTime, setLastDeleteTime] = useState(0);
    
    // 🔄 ПЕРИОДИЧЕСКАЯ СИНХРОНИЗАЦИЯ с сервером (каждые 30 секунд)
    useEffect(() => {
        const syncInterval = setInterval(async () => {
            try {
                // 🚫 Пропускаем синхронизацию если недавно удаляли товар
                const timeSinceLastDelete = Date.now() - lastDeleteTime;
                if (timeSinceLastDelete < 2000) { // 2 секунды
                    console.log(`🔄 [Cart Sync] Skipping sync - recent delete (${timeSinceLastDelete}ms ago)`);
                    return;
                }
                
                const serverCart = await cartService.getCurrentCartQuiet();
                if (serverCart && order) {
                    const serverProducts = serverCart.products || [];
                    const localProducts = order.products || [];
                    
                    // Проверяем различия между локальной и серверной корзиной
                    const serverProductIds = new Set(serverProducts.map(p => p.productId));
                    const localProductIds = new Set(localProducts.map(p => p.productId));
                    
                    // Проверяем количество товаров и их состав
                    const differentCount = serverProducts.length !== localProducts.length;
                    const differentProducts = 
                        serverProductIds.size !== localProductIds.size ||
                        [...serverProductIds].some(id => !localProductIds.has(id)) ||
                        [...localProductIds].some(id => !serverProductIds.has(id));
                    
                    // Проверяем различия в количестве товаров
                    const quantityDifferences = localProducts.some(localProduct => {
                        const serverProduct = serverProducts.find(p => p.productId === localProduct.productId);
                        return serverProduct && serverProduct.productCount !== localProduct.productCount;
                    });
                    
                    if (differentCount || differentProducts || quantityDifferences) {
                        console.log(`🔄 [Cart Sync] Detected differences:`, {
                            serverCount: serverProducts.length,
                            localCount: localProducts.length,
                            differentProducts,
                            quantityDifferences,
                        });
                        console.log(`🔄 [Cart Sync] Syncing with server...`);
                        setOrder(serverCart);
                    }
                }
            } catch (error) {
                console.warn('🔄 [Cart Sync] Failed to sync with server:', error);
            }
        }, 30000); // 30 секунд
        
        return () => clearInterval(syncInterval);
    }, [order]);

    // Функции для обработки пошлины (как на странице товара)
    const parseDuty = (dutyString) => {
        if (!dutyString) return 0;
        
        // Если это уже число
        if (typeof dutyString === 'number') {
            return dutyString > 1 ? dutyString / 100 : dutyString;
        }
        
        // Конвертируем в строку для обработки
        const str = String(dutyString).trim();
        if (!str) return 0;
        
        // Пробуем разные форматы
        const match = str.match(/(\d+\.?\d*)/); // Ищем любое число (включая с точкой)
        const result = match ? parseFloat(match[1]) / 100 : 0;
        
        return result;
    };

    const calculatePriceWithDuty = (priceValue, duty) => {
        const dutyFactor = parseDuty(duty);
        const finalPrice = priceValue * (1 + dutyFactor);
        return finalPrice;
    };

    const getDisplayPrice = (priceValue, duty) => {
        return calculatePriceWithDuty(priceValue, duty).toFixed(2);
    };

    // Функция для получения цен через WebSocket
    const loadProductPrices = useCallback((productId, partNumber) => {
        setPricesLoading(prev => new Set([...prev, productId]));
        
        // Создаем observer для этого товара
        const priceObserver = (priceData) => {
            console.log(`🌐 [WEBSOCKET RESPONSE] Received data for productId: ${productId}, partNumber: ${partNumber}`);
            console.log(`📥 [RAW DATA] Type: ${typeof priceData}, IsArray: ${Array.isArray(priceData)}, Length: ${priceData?.length || 'N/A'}`);
            
            // ФИЛЬТРАЦИЯ: проверяем что данные действительно для этого товара
            if (Array.isArray(priceData) && priceData.length > 0) {
                // Проверяем productId первого элемента данных  
                const firstItemProductId = priceData[0]?.productId;
                console.log(`🔍 [ID CHECK] Received productId: ${firstItemProductId}, Expected: ${productId}, Match: ${firstItemProductId === productId}`);
                
                if (firstItemProductId !== productId) {
                    console.log(`🚫 [Cart Observer] Ignoring prices for ${firstItemProductId}, expected ${productId}`);
                    return; // Игнорируем чужие данные
                }
                console.log(`✅ [Cart Observer] Prices confirmed for ${productId}: ${priceData.length} suppliers`);
                
                // ДЕТАЛЬНАЯ ДИАГНОСТИКА КАЖДОГО ПОСТАВЩИКА
                console.log(`🏢 [SUPPLIERS ANALYSIS] All suppliers for ${productId}:`);
                priceData.forEach((supplier, index) => {
                    console.log(`🏢 [SUPPLIER ${index}] ${supplier?.distributor || supplier?.source}:`, {
                        productId: supplier?.productId,
                        partNumber: supplier?.partNumber,
                        quantitiesCount: supplier?.quantities?.length || 0,
                        firstPrice: supplier?.quantities?.[0]?.priceValue,
                        firstPriceId: supplier?.quantities?.[0]?.id,
                        allPriceIds: supplier?.quantities?.map(q => q.id) || [],
                    });
                });
            } else {
                console.log(`⚠️ [EMPTY DATA] Received empty or invalid data for ${productId}`);
            }
            
            if (Array.isArray(priceData)) {
                console.log(`📦 [Cart Observer] Setting prices for ${productId}:`, {
                    suppliersCount: priceData.length,
                    firstSupplier: priceData[0]?.distributor || priceData[0]?.source,
                    firstPrice: priceData[0]?.quantities?.[0]?.priceValue,
                    firstProductId: priceData[0]?.productId,
                });
                
                // Проверяем ссылку на массив
                console.log(`🔗 [Reference Check] PriceData reference for ${productId}:`, priceData);
                
                setProductPrices(prev => {
                    // ЗАЩИТА: не перезаписываем данные пустыми массивами
                    if (priceData.length === 0 && prev[productId] && prev[productId].length > 0) {
                        console.log(`🛡️ [Protection] Skipping empty data for ${productId} - preserving existing ${prev[productId].length} suppliers`);
                        return prev; // Не изменяем состояние
                    }
                    const newPrices = {
                        ...prev,
                        [productId]: [...priceData], // Создаем копию массива
                    };
                    console.log(`📦 [Cart Observer] Updated productPrices state:`, {
                        targetProductId: productId,
                        allKeys: Object.keys(newPrices),
                        targetDataLength: newPrices[productId]?.length,
                    });
                    
                    // Детальное логирование состояния после обновления
                    console.log(`🔍 [State Update] Full state after setting ${productId}:`);
                    Object.keys(newPrices).forEach(key => {
                        const data = newPrices[key];
                        console.log(`📋 [State] "${key}" -> Length: ${data?.length || 0}, First ID: ${data?.[0]?.productId || 'undefined'}, Reference: ${data}`);
                    });
                    
                    // Проверяем, что данные под правильным ключом
                    const checkData = newPrices[productId];
                    console.log(`✅ [Verification] Data for ${productId}:`, {
                        exists: !!checkData,
                        length: checkData?.length || 0,
                        firstElementProductId: checkData?.[0]?.productId,
                        isArray: Array.isArray(checkData),
                        reference: checkData,
                    });
                    
                    return newPrices;
                });
            } else {
                console.warn(`❌ No prices for product ${productId}`);
                setProductPrices(prev => ({
                    ...prev,
                    [productId]: [],
                }));
            }
            
            setPricesLoading(prev => {
                const newSet = new Set(prev);
                newSet.delete(productId);
                return newSet;
            });
        };
        
        // Подписываемся на цены
        registerObserver(priceObserver);
        
        // Запускаем WebSocket для получения цен
        handleCreateNewPriceAsyncService(productId, partNumber);
        
        return () => {
            unRegisterObserver(priceObserver);
        };
    }, []);

    // Функция для расчета общей стоимости корзины с учетом пошлины
    const calculateTotalCostWithDuty = (products) => {
        if (!products || products.length === 0) return 0;
        
        return products.reduce((total, product) => {
            // Получаем цены для товара
            const prices = productPrices[product.productId] || [];
            
            if (prices.length === 0) {
                // Если нет цен от WebSocket, используем fallback
                const priceWithDuty = calculatePriceWithDuty(product.productPrice || 0, product.duty);
                return total + (priceWithDuty * product.productCount);
            }
            
            // Найдем цену от выбранного поставщика
            const bestPrice = getBestPriceForQuantity(prices, product.productCount, product.productPriceId);
            if (bestPrice) {
                // Применяем пошлину ОДИН раз (как в renderResponsiveTableRow на странице товара)
                const priceWithDuty = calculatePriceWithDuty(bestPrice.priceValue, product.duty);
                
                console.log(`🔧 [TotalCost] Single duty: ${bestPrice.priceValue} → ${priceWithDuty}`);
                
                return total + (priceWithDuty * product.productCount);
            }
            
            return total;
        }, 0);
    };
    
    // Функция для поиска цены от конкретного поставщика (как в PricesList)
    const getBestPriceForQuantity = (prices, quantity, productPriceId = null) => {
        console.log(`🔧 [getBestPrice] Input:`, {
            pricesLength: prices?.length || 0,
            quantity,
            productPriceId,
            firstSupplier: prices?.[0]?.distributor || prices?.[0]?.source,
            firstProductId: prices?.[0]?.productId,
            firstPriceValue: prices?.[0]?.quantities?.[0]?.priceValue,
        });
        
        if (!prices || prices.length === 0) return null;

        // НОВАЯ ЛОГИКА: Находим поставщика по productPriceId (как в PricesList)
        console.log(`🔧 [getBestPrice SEARCH] Looking for productPriceId: ${productPriceId} in ${prices.length} suppliers`);
        
        let selectedSupplier = null;
        if (productPriceId) {
            // ДЕТАЛЬНЫЙ ПОИСК ПО ВСЕМ ПОСТАВЩИКАМ
            prices.forEach((supplier, index) => {
                console.log(`🔧 [SUPPLIER SEARCH ${index}] ${supplier.distributor || supplier.source}:`, {
                    quantitiesCount: supplier.quantities?.length || 0,
                    allPriceIds: supplier.quantities?.map(q => q.id) || [],
                    searchingFor: productPriceId,
                });
            });
            
            // Ищем поставщика, у которого есть этот priceId
            for (const supplier of prices) {
                const foundQuantity = supplier.quantities?.find(q => q.id === productPriceId);
                if (foundQuantity) {
                    selectedSupplier = supplier;
                    console.log(`🔧 [getBestPrice] Found supplier for priceId ${productPriceId}:`, {
                        supplier: supplier.distributor || supplier.source,
                        foundQuantity: foundQuantity,
                    });
                    break;
                }
            }
            
            if (!selectedSupplier) {
                console.log(`⚠️ [getBestPrice] NO SUPPLIER FOUND for productPriceId: ${productPriceId}`);
            }
        } else {
            console.log(`⚠️ [getBestPrice] No productPriceId provided`);
        }

        // Если не нашли по productPriceId, берем первого доступного поставщика
        if (!selectedSupplier) {
            const filteredPrices = prices.filter(price =>
                !blockedStocks.includes(price.distributor || price.source),
            );
            
            if (filteredPrices.length === 0) return null;
            
            // 🔧 ВРЕМЕННОЕ РЕШЕНИЕ: ищем DigiKey по имени поставщика (как на странице товара)
            const digiKeySupplier = filteredPrices.find(price => 
                (price.distributor || price.source) === 'DigiKey',
            );
            
            if (digiKeySupplier) {
                selectedSupplier = digiKeySupplier;
                console.log(`🔧 [getBestPrice] WORKAROUND: Found DigiKey by name (productPriceId missing from backend):`, {
                    supplier: selectedSupplier.distributor || selectedSupplier.source,
                });
            } else {
                selectedSupplier = filteredPrices[0];
                console.log(`🔧 [getBestPrice] No DigiKey found, using first available:`, {
                    supplier: selectedSupplier.distributor || selectedSupplier.source,
                });
            }
        }

        // Проверяем, что поставщик не заблокирован
        const supplierKey = selectedSupplier.distributor || selectedSupplier.source;
        if (blockedStocks.includes(supplierKey)) {
            return null;
        }

        // Ищем подходящую цену у выбранного поставщика
        const coef = stockCoefs[supplierKey] ?? 1;
        console.log(`🔧 [getBestPrice] Using supplier: ${supplierKey}, coef: ${coef}, quantities: ${selectedSupplier.quantities.length}`);
        
        const sortedQuantities = selectedSupplier.quantities.sort(sortByQuantity);
        
        // Найдем подходящий ценовой уровень
        for (let i = sortedQuantities.length - 1; i >= 0; i--) {
            const priceLevel = sortedQuantities[i];
            
            if (priceLevel.minQty <= quantity) {
                // Применяем коэффициент склада
                const adjustedPrice = priceLevel.priceValue * coef;
                
                console.log(`🔧 [getBestPrice] Found price for ${quantity}: ${priceLevel.priceValue} * ${coef} = ${adjustedPrice}`);
                
                return {
                    ...priceLevel,
                    priceValue: adjustedPrice,
                    supplier: supplierKey,
                };
            }
        }
        
        return null;
    };

    // Получаем корзину через сервис
    const getCartOrder = async () => {
        setLoading(true);
        try {
            const cart = await cartService.getCurrentCart();
            setOrder(cart);
            setError('');
        } catch (err) {
            console.error('Ошибка загрузки корзины:', err);
            setError('Не удалось загрузить корзину');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        getCartOrder();
        
        // Подписываемся на изменения корзины
        const handleCartUpdate = (cart) => {
            // 🚫 Игнорируем обновления корзины если недавно удаляли товар
            const timeSinceLastDelete = Date.now() - lastDeleteTime;
            if (timeSinceLastDelete < 2000) { // 2 секунды
                console.log(`🚫 [Cart Update] Ignoring cart update - recent delete (${timeSinceLastDelete}ms ago)`);
                return;
            }
            
            console.log(`🔄 [Cart Update] Received cart update from cartService:`, {
                productsCount: cart?.products?.length || 0,
                cartId: cart?.id,
            });
            setOrder(cart);
        };
        
        cartService.subscribe(handleCartUpdate);
        
        // Подписываемся на изменения cartManager
        const handleCartManagerUpdate = () => {
            setActivePricesChanged(prev => !prev);
        };
        
        cartManager.registerCartObserver(handleCartManagerUpdate);
        
        return () => {
            cartService.unsubscribe(handleCartUpdate);
            cartManager.unRegisterCartObserver(handleCartManagerUpdate);
            clearPricesAsyncService();
            // 🔧 Очистка всех debounce таймеров при размонтировании
            quantityTimeouts.forEach(timeoutId => clearTimeout(timeoutId));
            quantityTimeouts.clear();
        };
    }, [quantityTimeouts]); // НЕ включаем lastDeleteTime чтобы избежать переподписки
    
    // Загружаем коэффициенты складов (как на странице товара)
    useEffect(() => {
        const errorCallback = (error) => {
            console.error('Error loading stock coefficients:', error);
        };
        
        getFetchRequest(
            makeUrl(['/api/v1/product/get-stocks-coef/']),
            response => {
                console.log('🔧 Loaded stock coefficients:', response);
                setStockCoefs(response);
            },
            errorCallback,
        );
        
        // Загружаем заблокированные склады (используем POST как на странице товара)
        const successGetBlockedStocks = (response) => {
            const blockedNames = response.names || [];
            console.log('🔧 Loaded blocked stocks:', blockedNames);
            setBlockedStocks(blockedNames);
        };
        
        // Используем postFetchRequest как на странице товара
        postFetchRequest(
            makeUrl(['/api/v1/product/get-stocks/']),
            {}, 
            successGetBlockedStocks,    
            errorCallback,
        );
    }, []);

    // Подтянуть данные из "Настроек": Профиль / Покупатель / Доставка
    useEffect(() => {
        const onSuccess = (response) => {
            if (!response || !response.id) return;
            setSettingsUserId(response.id);
            setProfileName(response.name || '');
            setProfilePhone(response.phone || '');
            setPriceCurrency(response.priceCurrency === 'USD' ? 'USD' : 'RUB');
        };
        getFetchRequest(makeUrl([GET_CURRENT_USER]), onSuccess, errorCallback);
    }, []);

    useEffect(() => {
        if (!settingsUserId) return;

        const customerUrl = makeUrl([MAKE_USER_REQUEST, settingsUserId, '/customer/']);
        const deliveryUrl = makeUrl([MAKE_USER_REQUEST, settingsUserId, '/delivery/']);

        const handleCustomers = (response) => {
            if (!Array.isArray(response) || response.length === 0) {
                setBuyerName('');
                return;
            }
            const defaultCustomer = response.find((it) => it && it.isDefault) || response[0];
            setBuyerName(defaultCustomer?.name || '');
        };

        const handleDeliveries = (response) => {
            if (!Array.isArray(response) || response.length === 0) {
                setRecipientName('');
                setRecipientPhone('');
                setDeliveryAddress('');
                return;
            }
            const defaultDelivery = response.find((it) => it && it.isDefault) || response[0];
            setRecipientName(defaultDelivery?.recipientName || '');
            setRecipientPhone(defaultDelivery?.recipientPhone || '');
            setDeliveryAddress(defaultDelivery?.deliveryAddress || '');
        };

        getFetchRequest(customerUrl, handleCustomers, errorCallback);
        getFetchRequest(deliveryUrl, handleDeliveries, errorCallback);
    }, [settingsUserId]);
    
    // 🔧 ОПТИМИЗАЦИЯ: создаем стабильную зависимость только на основе productId списка
    const productIdentifiers = useMemo(() => {
        if (!order || !order.products) return [];
        return order.products.map(product => ({
            productId: product.productId,
            productName: product.productName,
            productPriceId: product.productPriceId,
        }));
    }, [order?.products?.map(p => `${p.productId}-${p.productName}-${p.productPriceId}`).join(',')]);

    // Загружаем цены для товаров в корзине через WebSocket
    useEffect(() => {
        if (!order || !order.products || order.products.length === 0) {
            return;
        }
        
        console.log(`🔄 Loading prices for ${order.products.length} products in cart`);
        
        // ДЕТАЛЬНОЕ ЛОГИРОВАНИЕ ТОВАРОВ В КОРЗИНЕ
        console.log(`🛒 [CART ANALYSIS] Full cart structure:`);
        order.products.forEach((product, index) => {
            console.log(`🛒 [CART PRODUCT ${index}] FULL STRUCTURE:`, {
                productId: product.productId,
                productName: product.productName,
                productPriceId: product.productPriceId,
                partNumber: product.partNumber,
                manufacturer: product.manufacturer,
                stockId: product.stockId,
                allFields: Object.keys(product),
                FULL_PRODUCT_OBJECT: product,
            });
            
            // ДЕТАЛЬНЫЙ АНАЛИЗ ВСЕХ ПОЛЕЙ
            console.log(`🔍 [CART PRODUCT ${index}] ALL FIELDS ANALYSIS:`);
            Object.keys(product).forEach(key => {
                console.log(`📋 [FIELD] "${key}": ${typeof product[key]} = ${product[key]}`);
            });
        });
        
        // Очищаем только цены по товарам, которых больше нет, оставшиеся сохраняем
        setProductPrices(prev => {
            const currentIds = new Set(order.products.map(p => p.productId));
            const next = {};
            Object.keys(prev || {}).forEach(pid => {
                if (currentIds.has(pid)) {
                    next[pid] = prev[pid];
                }
            });
            return next;
        });
        
        const unsubscribeFunctions = [];
        
        // Загружаем цены для каждого товара
        order.products.forEach((product, index) => {
            if (product.productId && product.productName) {
                console.log(`🌐 [WEBSOCKET REQUEST ${index}] Loading prices for:`, {
                    requestedProductId: product.productId,
                    requestedProductName: product.productName,
                    hasProductPriceId: !!product.productPriceId,
                    productPriceId: product.productPriceId,
                });
                const unsubscribe = loadProductPrices(product.productId, product.productName);
                unsubscribeFunctions.push(unsubscribe);
            }
        });
        
        return () => {
            unsubscribeFunctions.forEach(unsubscribe => unsubscribe());
        };
    }, [productIdentifiers, loadProductPrices]);

    const handleChangeQuantity = useCallback(async (step, index, value = null) => {
        if (!order || !order.products || !order.products[index]) return;
        
        if (order.products[index].productCount === 0 && step === -1) {
            return;
        }
        
        let newQuantityValue;
        if (step !== 0) {
            newQuantityValue = order.products[index].productCount + step;
        } else {
            newQuantityValue = value;
        }
        // Ограничиваем верхней границей доступного остатка, если известен
        const productForLimit = order.products[index];
        const availabilityForLimit = getProductAvailability(productForLimit.productId, productForLimit.productPriceId);
        const maxStock = availabilityForLimit && typeof availabilityForLimit.stockNumber === 'number' && availabilityForLimit.stockNumber > 0
            ? availabilityForLimit.stockNumber
            : null;
        if (maxStock !== null) {
            newQuantityValue = Math.min(newQuantityValue, maxStock);
        }

        newQuantityValue = Math.max(0, newQuantityValue);
        
        // Если количество стало 0, удаляем товар
        if (newQuantityValue === 0) {
            handleRemoveProduct(index);
            return;
        }
        
        const product = order.products[index];
        const debounceKey = `${product.productId}-${product.stockId}`;
        
        // 🚀 МГНОВЕННОЕ ОБНОВЛЕНИЕ UI (оптимистичное обновление)
        const updatedOrder = {
            ...order,
            products: order.products.map((p, i) => 
                i === index ? { ...p, productCount: newQuantityValue } : p,
            ),
        };
        setOrder(updatedOrder);
        
        // 🔧 DEBOUNCE API ЗАПРОСА (задержка 300ms)
        if (quantityTimeouts.has(debounceKey)) {
            clearTimeout(quantityTimeouts.get(debounceKey));
        }
        
        const timeoutId = setTimeout(async () => {
            try {
                // 🔧 ТИХОЕ ОБНОВЛЕНИЕ: не перезагружаем всю корзину
                await cartService.updateProductQuantityQuiet(
                    product.productId, 
                    product.stockId, 
                    newQuantityValue,
                );
                console.log(`✅ [Quantity Updated] ${product.productName}: ${newQuantityValue}`);
            } catch (error) {
                console.error('Ошибка изменения количества:', error);
                setError('Не удалось изменить количество товара');
                // 🚫 ТОЛЬКО ПРИ ОШИБКЕ откатываем ТИХО (БЕЗ ПЕРЕЗАГРУЗКИ)
                try {
                    const originalOrder = await cartService.getCurrentCartQuiet();
                    if (originalOrder) {
                        setOrder(originalOrder);
                        console.log(`✅ [QUANTITY ROLLBACK] UI quietly restored after error`);
                    }
                } catch (rollbackError) {
                    console.error(`❌ [QUANTITY ROLLBACK FAILED]`, rollbackError);
                }
            } finally {
                quantityTimeouts.delete(debounceKey);
            }
        }, 300);
        
        quantityTimeouts.set(debounceKey, timeoutId);
    }, [order, quantityTimeouts, getProductAvailability]);

    const handleRemoveProduct = useCallback(async (index) => {
        if (!order || !order.products || !order.products[index]) return;
        
        const product = order.products[index];
        console.log(`🗑️ [DELETE] Starting removal of ${product.productName} at index ${index}:`, {
            productId: product.productId,
            stockId: product.stockId,
            distributorId: product.distributorId,
            manufacturerName: product.manufacturerName,
        });
        
        // 🔒 Временно блокируем обновления корзины на 2 секунды
        const deleteTime = Date.now();
        setLastDeleteTime(deleteTime);
        
        // 🚀 Оптимистичное удаление из UI
        const newProducts = order.products.filter((_, i) => i !== index);
        setOrder({
            ...order,
            products: newProducts,
        });
        
        // 💾 Сохраняем текущие цены в backup перед удалением
        pricesBackupRef.current = { ...productPrices };
        console.log(`💾 [BACKUP] Saved current prices:`, Object.keys(pricesBackupRef.current));
        
        // 🗑️ Очищаем данные ТОЛЬКО если это последний товар с таким productId (цены общие для продукта)
        const remainingProductsWithSameProduct = newProducts.filter(p => p.productId === product.productId);
        if (remainingProductsWithSameProduct.length === 0) {
            console.log(`🗑️ [CLEANUP] Removing prices and loading state for ${product.productId} (no more items with this productId)`);
            setProductPrices(prev => {
                const updated = { ...prev };
                delete updated[product.productId];
                pricesBackupRef.current = updated; // Обновляем backup
                return updated;
            });
            
            setPricesLoading(prev => {
                const updated = new Set(prev);
                updated.delete(product.productId);
                return updated;
            });
        } else {
            console.log(`🔄 [KEEP] Keeping prices for ${product.productId} (${remainingProductsWithSameProduct.length} items remain with this productId)`);
        }
        
        try {
            // 🌐 Удаляем на сервере
            await cartService.removeProductFromCartQuiet(product.productId, product.stockId, {
                productPriceId: product.productPriceId,
                distributorId: product.distributorId,
                packerId: product.packerId,
                manufacturerName: product.manufacturerName,
            });
            console.log(`✅ [DELETE SUCCESS] ${product.productName} removed successfully`);
            
            // 🔄 Мягко обновляем цены: восстанавливаем только для оставшихся товаров
            setTimeout(() => {
                if (Object.keys(pricesBackupRef.current).length > 0) {
                    const currentIds = new Set(newProducts.map(p => p.productId));
                    const restored = {};
                    Object.entries(pricesBackupRef.current).forEach(([pid, data]) => {
                        if (currentIds.has(pid)) {
                            restored[pid] = data;
                        }
                    });
                    console.log(`🔄 [RESTORE] Restoring preserved prices:`, Object.keys(restored));
                    setProductPrices(restored);
                }
            }, 50);
            
        } catch (error) {
            console.error(`❌ [DELETE FAILED] ${product.productName}:`, error);
            setError(`Не удалось удалить товар: ${error.message}`);
            
            // 🔄 Восстанавливаем товар в UI при ошибке
            setOrder(order); // Возвращаем исходное состояние
        }
    }, [order]);

    // Оформить заказ (изменить статус на PLACED)
    const handleSubmitOrder = async () => {
        if (!order || !order.id || submittingOrder) return;
        setSubmittingOrder(true);
        
        console.log(`🛒 [SUBMIT ORDER] Starting order submission:`, {
            orderId: order.id,
            productsCount: order.products?.length || 0,
            products: order.products?.map(p => ({
                productName: p.productName,
                productCount: p.productCount,
                distributorId: p.distributorId,
                packerId: p.packerId,
                manufacturerName: p.manufacturerName,
                stockId: p.stockId,
            })) || [],
        });
        
        try {
            await cartService.submitOrder();
            console.log(`✅ [SUBMIT ORDER] Order ${order.id} successfully submitted`);
            // После успешного оформления переходим к заказам
            navigate('/orders');
        } catch (error) {
            console.error('❌ [SUBMIT ORDER] Failed to submit order:', error);
            setError('Не удалось оформить заказ');
            setSubmittingOrder(false);
        }
    };

    // Показываем загрузку
    if (loading) {
        return (
            <section className={`workspace workspace_align cart-page orders-page ${isCollapsed === null ? '' : isCollapsed ? 'workspace_collapsed' : 'workspace_full-width'}`}>
                <nav className="navigation-block container">
                    <div className="navigation-group">
                        <a href="#" className="navigation-item navigation-goback" onClick={e => { e.preventDefault(); navigate(-1); }}>Назад</a>
                        <ChevronIconNavigation nameOfClass="navigation-icon__separate" />
                        <a href="#" className="navigation-item" onClick={e => { e.preventDefault(); navigate('/home'); }}>Главная</a>
                        <ChevronIconNavigation nameOfClass="navigation-icon__separate" />
                        <a className="navigation-item navigation-item_current" href="#">Корзина</a>
                    </div>
                </nav>
                <div className="order-detail-container">
                    <div className="order-detail-info">
                        <h2>Загрузка корзины...</h2>
                    </div>
                </div>
            </section>
        );
    }

    // Показываем ошибку
    if (error) {
        return (
            <section className={`workspace workspace_align cart-page orders-page ${isCollapsed === null ? '' : isCollapsed ? 'workspace_collapsed' : 'workspace_full-width'}`}>
                <nav className="navigation-block container">
                    <div className="navigation-group">
                        <a href="#" className="navigation-item navigation-goback" onClick={e => { e.preventDefault(); navigate(-1); }}>Назад</a>
                        <ChevronIconNavigation nameOfClass="navigation-icon__separate" />
                        <a href="#" className="navigation-item" onClick={e => { e.preventDefault(); navigate('/home'); }}>Главная</a>
                        <ChevronIconNavigation nameOfClass="navigation-icon__separate" />
                        <a className="navigation-item navigation-item_current" href="#">Корзина</a>
                    </div>
                </nav>
                <div className="order-detail-container">
                    <div className="order-detail-info">
                        <h2>Ошибка: {error}</h2>
                        <button onClick={getCartOrder} className="order-detail-btn">Попробовать снова</button>
                    </div>
                </div>
            </section>
        );
    }

    // Показываем пустую корзину
    if (!order || !order.products || order.products.length === 0) {
        return (
            <section className={`workspace workspace_align cart-page orders-page ${isCollapsed === null ? '' : isCollapsed ? 'workspace_collapsed' : 'workspace_full-width'}`}>
                <nav className="navigation-block container">
                    <div className="navigation-group">
                        <a href="#" className="navigation-item navigation-goback" onClick={e => { e.preventDefault(); navigate(-1); }}>Назад</a>
                        <ChevronIconNavigation nameOfClass="navigation-icon__separate" />
                        <a href="#" className="navigation-item" onClick={e => { e.preventDefault(); navigate('/home'); }}>Главная</a>
                        <ChevronIconNavigation nameOfClass="navigation-icon__separate" />
                        <a className="navigation-item navigation-item_current" href="#">Корзина</a>
                    </div>
                </nav>
                <div className="order-detail-container">
                    <div className="order-detail-info">
                        <h2>Корзина пуста</h2>
                        <p>Добавьте товары в корзину для оформления заказа</p>
                        <button onClick={() => navigate('/catalog')} className="order-detail-btn order-detail-btn-submit">
                            Перейти к каталогу
                        </button>
                    </div>
                </div>
            </section>
        );
    }

    return (
        <section className={`workspace workspace_align cart-page orders-page ${isCollapsed === null ? '' : isCollapsed ? 'workspace_collapsed' : 'workspace_full-width'}`}>
            <nav className="navigation-block container">
                <div className="navigation-group">
                    <a href="#" className="navigation-item navigation-goback" onClick={e => { e.preventDefault(); navigate(-1); }}>Назад</a>
                    <ChevronIconNavigation nameOfClass="navigation-icon__separate" />
                    <a href="#" className="navigation-item" onClick={e => { e.preventDefault(); navigate('/home'); }}>Главная</a>
                    <ChevronIconNavigation nameOfClass="navigation-icon__separate" />
                    <a className="navigation-item navigation-item_current" href="#">Корзина</a>
                </div>
            </nav>

            <div className="order-detail-container">
                <div className="order-detail-header">
                    <button className="order-detail-back-btn" onClick={() => navigate(-1)}>
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M1 15L14.5 1M1 1L14.5 15" stroke="#1C1C1C"/>
                        </svg>
                    </button>
                </div>
                
                {(() => {
                    const totalCostWithDuty = calculateTotalCostWithDuty(order.products);
                    const isFreeDelivery = totalCostWithDuty >= 5000;
                    const deliveryMethodText = isFreeDelivery ? 'Бесплатная доставка до адреса' : 'Самовывоз';

                    return (
                        <div className="order-detail-info">
                            <h2>Всего {order.products ? order.products.length : 0} товара на сумму:</h2>
                            <div className="order-detail-total">
                                {totalCostWithDuty.toFixed(2)} {priceCurrency === 'USD' ? '$' : '₽'}
                            </div>
                            
                            <div className="order-detail-meta">
                                <div className="order-detail-field">
                                    <span className="order-detail-label">Заказ №:</span>
                                    <span className="order-detail-value">{order.orderNumber}</span>
                                </div>
                                <div className="order-detail-field">
                                    <span className="order-detail-label">Покупатель:</span>
                                    <span className="order-detail-value">{buyerName || '—'}</span>
                                </div>
                                <div className="order-detail-field">
                                    <span className="order-detail-label">Способ получения:</span>
                                    <span className={`order-detail-value ${isFreeDelivery ? 'order-detail-value_free-delivery' : ''}`}>
                                        {deliveryMethodText}
                                    </span>
                                </div>
                                <div className="order-detail-field">
                                    <span className="order-detail-label">Получатель:</span>
                                    <span className="order-detail-value">{recipientName || profileName || '—'}</span>
                                </div>
                                <div className="order-detail-field">
                                    <span className="order-detail-label">Контактный телефон:</span>
                                    <span className="order-detail-value">{recipientPhone || profilePhone || '—'}</span>
                                </div>
                                <div className="order-detail-field">
                                    <span className="order-detail-label">Адрес доставки:</span>
                                    <span className="order-detail-value">{deliveryAddress || '—'}</span>
                                </div>
                            </div>
                            
                            <div className="order-detail-status">
                                Статус заказа: Создан
                            </div>
                        </div>
                    );
                })()}

                <div className="order-detail-products">
                    <div className="order-detail-products-layout">
                        <div className="order-detail-received-container">
                            <table className="order-detail-table">
                                <thead>
                                    <tr>
                                        <th>№</th>
                                        <th>Наименование товара / Производитель</th>
                                        <th>Наличие</th>
                                        <th>Правила заказа</th>
                                        <th>Отгрузка</th>
                                        <th>Цена</th>
                                        <th>Добавить в заказ</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {order.products && order.products.map((product, index) => {
                                        // Логируем структуру товара для отладки
                                        console.log(`🛒 [Cart Product ${index}] Structure:`, {
                                            productId: product.productId,
                                            productName: product.productName,
                                            productPriceId: product.productPriceId,
                                            manufacturer: product.manufacturer,
                                            // 🔧 НОВЫЕ ПОЛЯ для расчета цен в заказе
                                            distributorId: product.distributorId,
                                            packerId: product.packerId,
                                            manufacturerName: product.manufacturerName,
                                            stockId: product.stockId,
                                            allFields: Object.keys(product),
                                        });
                                        
                                        // 🏪 Логируем данные о товаре из цен
                                        const availability = getProductAvailability(product.productId, product.productPriceId);
                                        const orderRules = getProductOrderRules(product.productId, product.productPriceId);
                                        const shipmentInfo = getProductShipmentInfo(product.productId, product.productPriceId);
                                        const manufacturerInfo = getProductManufacturer(product.productId, product.productPriceId, product.manufacturer);
                                        
                                        console.log(`🏪 [Product Data ${index}] Extracted info:`, {
                                            productName: product.productName,
                                            existingManufacturer: product.manufacturer,
                                            extractedManufacturer: manufacturerInfo.manufacturer,
                                            availability: availability,
                                            orderRules: orderRules,
                                            shipmentInfo: shipmentInfo,
                                        });
                                        
                                        return (
                                            <tr key={`${product.productId}-${product.stockId || product.productPriceId || index}`}>
                                                <td>{index + 1}</td>
                                                <td>
                                                    <div className="product-info-row">
                                                        <div className="product-details">
                                                            <Link 
                                                                to={`/catalog/product/${product.productId}`}
                                                                className="product-name-link product-name"
                                                            >
                                                                {(() => {
                                                                    const manufacturerInfo = getProductManufacturer(product.productId, product.productPriceId, product.manufacturer);
                                                                    const manufacturer = manufacturerInfo.manufacturer;
                                                                    
                                                                    if (manufacturerInfo.isLoading) {
                                                                        return `${product.productName} (Загрузка...)`;
                                                                    }
                                                                    
                                                                    if (manufacturer) {
                                                                        return `${product.productName} (${manufacturer})`;
                                                                    }
                                                                    
                                                                    return product.productName;
                                                                })()}
                                                            </Link>
                                                        </div>
                                                        {product.productImage ? (
                                                            <div className="product-image">
                                                                <img 
                                                                    src={handleGetImageUrl(product.productId, product.productImage)} 
                                                                    alt={product.productName}
                                                                    onError={(e) => { e.target.onerror = null; e.target.src = EMPTY_IMAGE_SRC; }}
                                                                />
                                                            </div>
                                                        ) : (
                                                            <div className="product-image product-image-placeholder">
                                                                <svg viewBox="0 0 60 50" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                                    <rect width="60" height="50" fill="#f0f0f0"/>
                                                                    <path d="M20 20h20v10h-20z" fill="#d0d0d0"/>
                                                                </svg>
                                                            </div>
                                                        )}
                                                    </div>
                                                </td>
                                                <td>
                                                    <div className="availability-info">
                                                        {(() => {
                                                            const availability = getProductAvailability(product.productId, product.productPriceId);
                                                            if (availability.isLoading) {
                                                                return <div className="availability-loading">Загрузка...</div>;
                                                            }
                                                            return (
                                                                <div className="availability-quantity">
                                                                    {availability.stockNumber ? `${availability.stockNumber} шт.` : 'Уточняется'}
                                                                </div>
                                                            );
                                                        })()}
                                                    </div>
                                                </td>
                                                <td>
                                                    <div className="order-rules">
                                                        {(() => {
                                                            const orderRules = getProductOrderRules(product.productId, product.productPriceId);
                                                            if (orderRules.isLoading) {
                                                                return <div className="order-rules-loading">Загрузка...</div>;
                                                            }
                                                            return (
                                                                <>
                                                                    <div>Мин. объем</div>
                                                                    <div>заказа {orderRules.minOrderQuantity || 1} шт.</div>
                                                                    <div>Кратность {orderRules.multiplicity || 1} шт.</div>
                                                                </>
                                                            );
                                                        })()}
                                                    </div>
                                                </td>
                                                <td>
                                                    <div className="shipment-info">
                                                        {(() => {
                                                            const shipmentInfo = getProductShipmentInfo(product.productId, product.productPriceId);
                                                            if (shipmentInfo.isLoading) {
                                                                return <div className="shipment-loading">Загрузка...</div>;
                                                            }
                                                            return (
                                                                <>
                                                                    <div className="shipment-time">{shipmentInfo.deliveryTime || '20-30 раб. дней'}</div>
                                                                    <div className="shipment-distributor">{shipmentInfo.distributor || 'Уточняется'}</div>
                                                                    {shipmentInfo.packer && (
                                                                        <div className="shipment-packer">Уп. {shipmentInfo.packer}</div>
                                                                    )}
                                                                </>
                                                            );
                                                        })()}
                                                    </div>
                                                </td>
                                                <td>
                                                    {(() => {
                                                        console.log(`🔍 [DEBUG] Before extraction for ${product.productName} (${product.productId}):`);
                                                        console.log('📋 [DEBUG] Product Prices State Keys:', Object.keys(productPrices));
                                                        console.log('📋 [DEBUG] Requested ID:', product.productId);
                                                        console.log('📋 [DEBUG] Direct access result:', productPrices[product.productId]);
                                                        
                                                        // Показываем содержимое каждого ключа
                                                        Object.keys(productPrices).forEach(key => {
                                                            const data = productPrices[key];
                                                            console.log(`📦 [DEBUG] Key: "${key}" -> Length: ${data?.length || 0}, First ID: ${data?.[0]?.productId || 'undefined'}`);
                                                        });
                                                        
                                                        const pricesForThisProduct = productPrices[product.productId] || [];
                                                        
                                                        // Дополнительная проверка извлеченных данных
                                                        console.log(`🎯 [Data Extraction] For ${product.productId}:`);
                                                        console.log(`📄 [Extracted] Reference:`, pricesForThisProduct);
                                                        console.log(`📊 [Extracted] First 2 elements:`, pricesForThisProduct.slice(0, 2).map(item => ({
                                                            productId: item?.productId,
                                                            supplier: item?.distributor || item?.source,
                                                        })));
                                                        
                                                        // Проверяем ссылки на идентичность
                                                        Object.keys(productPrices).forEach(key => {
                                                            if (key !== product.productId && productPrices[key] === pricesForThisProduct) {
                                                                console.log(`⚠️ [Reference Issue] Same reference for ${product.productId} and ${key}!`);
                                                            }
                                                        });
                                                        
                                                        console.log(`🎨 [UI] Rendering PricesList for ${product.productName} (${product.productId}):`, {
                                                            pricesCount: pricesForThisProduct.length,
                                                            firstSupplierName: pricesForThisProduct[0]?.distributor || pricesForThisProduct[0]?.source,
                                                            firstPrice: pricesForThisProduct[0]?.quantities?.[0]?.priceValue,
                                                            allProductPricesKeys: Object.keys(productPrices),
                                                            firstDataProductId: pricesForThisProduct[0]?.productId,
                                                            productPricesEntries: Object.entries(productPrices).map(([key, value]) => ({
                                                                key,
                                                                length: value?.length || 0,
                                                                firstSupplier: value?.[0]?.distributor || value?.[0]?.source,
                                                            })),
                                                        });
                                                        return (
                                                            <PricesList
                                                                pricesArray={pricesForThisProduct}
                                                                activePricesChanged={activePricesChanged}
                                                                currentQuantity={product.productCount}
                                                                duty={product.duty}
                                                                stockCoefs={stockCoefs}
                                                                blockedStocks={blockedStocks}
                                                                productPriceId={product.productPriceId}
                                                                currencySign={priceCurrency === 'USD' ? '$' : '₽'}
                                                            />
                                                        );
                                                    })()}
                                                </td>
                                                <td className="product-price__table-item product-price__table-item_height">
                                                    <div className={`product-price__table-block ${product.productCount === 0 ? 'disabled' : ''}`}>
                                                        <span className="product-price__table-button product-price__table-button_left"
                                                            onClick={() => product.productCount === 0 ? null : handleChangeQuantity(-1, index, 0)}>
                                                            <svg className={`product-price__table-icon ${product.productCount !== 0 ? '' : 'product-price__table-icon_disabled'}`} 
                                                                viewBox="0 0 12 12" xmlns="http://www.w3.org/2000/svg">
                                                                <path d="M10.5 6.21428H6.71429L5.28571 6.21428L1.5 6.21428V5.78571H5.28571L6.71429 5.78571L10.5 5.78571V6.21428Z"></path>
                                                            </svg>
                                                        </span>
                                                        <input className="product-price__table-quantity"
                                                            value={product.productCount}
                                                            onChange={(e) => {
                                                                const raw = e.target.value;
                                                                if (raw === '') {
                                                                    return; // временно допускаем пустой ввод без изменений
                                                                }
                                                                const parsed = parseInt(raw, 10);
                                                                const availability = getProductAvailability(product.productId, product.productPriceId);
                                                                const maxStock = availability && typeof availability.stockNumber === 'number' && availability.stockNumber > 0
                                                                    ? availability.stockNumber
                                                                    : null;
                                                                let nextValue = Number.isNaN(parsed) ? product.productCount : parsed;
                                                                if (maxStock !== null && nextValue > maxStock) {
                                                                    nextValue = maxStock;
                                                                }
                                                                handleChangeQuantity(0, index, nextValue);
                                                            }}
                                                        />
                                                        <span 
                                                            className="product-price__table-button"
                                                            onClick={() => handleChangeQuantity(1, index, 0)}>
                                                            <svg className="product-price__table-icon" 
                                                                viewBox="0 0 12 12" xmlns="http://www.w3.org/2000/svg">
                                                                <path d="M6.71429 5.78571H10.5V6.21429H6.71429H6.21429V6.71429V10.5H5.78571V6.71429V6.21429H5.28571H1.5V5.78571H5.28571H5.78571V5.28571V1.5H6.21429V5.28571V5.78571H6.71429Z"></path>
                                                            </svg>
                                                        </span>
                                                    </div>
                                                    <button className="quantity-remove-btn" onClick={() => handleRemoveProduct(index)}>
                                                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                            <path d="M1 15L14.5 1M1 1L14.5 15" stroke="#1C1C1C"/>
                                                        </svg>  
                                                    </button>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>

                        <div className="order-total-sidebar">
                            <div className="order-total-header">Общая стоимость товара</div>
                            <div className="order-total-items">
                                {order.products && order.products.map((product, idx) => (
                                    <div key={`${product.productId}-${product.stockId || product.productPriceId || idx}`} className="order-total-item">
                                        <div className="order-total-quantity">
                                            {product.productCount} шт.
                                        </div>
                                        <span className="order-total-between">×</span>
                                        <div className="order-total-price">
                                            {(() => {
                                                const prices = productPrices[product.productId] || [];
                                                const bestPrice = getBestPriceForQuantity(prices, product.productCount, product.productPriceId);
                                                const priceValue = bestPrice ? bestPrice.priceValue : (product.productPrice || 0);
                                                // Изменено: возвращаем цену за единицу вместо общей стоимости
                                                return calculatePriceWithDuty(priceValue, product.duty).toFixed(2);
                                            })()} {priceCurrency === 'USD' ? '$' : '₽'}
                                        </div>
                                        <div className="order-total-price-with-tax">
                                            <span>Итого с НДС:</span> <span>
                                                {(() => {
                                                    const prices = productPrices[product.productId] || [];
                                                    const bestPrice = getBestPriceForQuantity(prices, product.productCount, product.productPriceId);
                                                    const priceValue = bestPrice ? bestPrice.priceValue : (product.productPrice || 0);
                                                    // Оставляем общую стоимость здесь
                                                    return (calculatePriceWithDuty(priceValue, product.duty) * product.productCount).toFixed(2);
                                                })()} {priceCurrency === 'USD' ? '$' : '₽'}
                                            </span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                    
                    <div className="order-detail-actions">
                        <button 
                            className="order-detail-btn order-detail-btn-add orders-button"
                            onClick={() => navigate('/catalog')}
                        >
                            Добавить товар
                        </button>
                        <div className="order-detail-total-final">
                            <span className='order-detail-total-final-label'>Всего с НДС:</span> 
                            <span className='order-detail-total-final-value'>
                                {calculateTotalCostWithDuty(order.products).toFixed(2)} {priceCurrency === 'USD' ? '$' : '₽'}
                            </span>
                        </div>
                        <button 
                            className="order-detail-btn order-detail-btn-submit"
                            onClick={handleSubmitOrder}
                            disabled={submittingOrder || !order || !order.products || order.products.length === 0}
                        >
                            {submittingOrder ? (
                                <span className="loader" aria-label="Оформление заказа">
                                    <span className="loader__inside"></span>
                                </span>
                            ) : 'Оформить заказ'}
                        </button>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default Cart; 